import { determineEventMode, type EventMode, type EventWindows } from "./event-mode";

export const MOBILE_EXPERIENCE_KINDS = [
  "attendee",
  "staff",
  "school",
  "presenter",
  "exhibitor",
  "fine_arts_director",
  "fine_arts_coordinator",
  "fine_arts_check_in",
  "fine_arts_judge",
  "fine_arts_room_monitor",
  "fine_arts_awards",
  "fine_arts_support",
] as const;

export type MobileExperienceKind = (typeof MOBILE_EXPERIENCE_KINDS)[number];

export type MobileProgramKind =
  | "convention"
  | "fine_arts"
  | "athletics"
  | "accreditation"
  | "certification"
  | "other";

export const MOBILE_ICON_KEYS = [
  "home",
  "calendar",
  "person",
  "map",
  "more",
  "staff",
  "school",
  "sessions",
  "materials",
  "issues",
  "people",
  "rules",
  "scoring",
  "room",
  "results",
  "messages",
  "help",
] as const;

export type MobileIconKey = (typeof MOBILE_ICON_KEYS)[number];

export interface MobileNavigationDestination {
  id: string;
  label: string;
  path: string;
  icon: MobileIconKey;
  routePrefixes: readonly string[];
}

export interface MobileExperienceCandidate {
  kind: MobileExperienceKind;
  label: string;
}

export interface MobileExperienceEventInput {
  id: string;
  slug: string;
  name: string;
  startsOn: string;
  programId: string;
  programKind: MobileProgramKind;
  windows: EventWindows | null;
  takeover: {
    enabled: boolean;
    priority: number;
    revision: number;
  };
  experiences: readonly MobileExperienceCandidate[];
}

export interface MobileExperiencePreference {
  eventId: string;
  experience: MobileExperienceKind;
}

export interface MobileExperienceInput {
  now: Date;
  events: readonly MobileExperienceEventInput[];
  preference: MobileExperiencePreference | null;
}

export type MobileExperienceSelectionReason =
  | "explicit"
  | "active_takeover"
  | "event_relevance"
  | "attendee_fallback"
  | "choice_required";

export interface MobileExperienceOption {
  kind: MobileExperienceKind;
  label: string;
  homePath: string;
  navigation: readonly MobileNavigationDestination[];
}

export interface MobileExperienceDecision {
  event: {
    id: string;
    slug: string;
    name: string;
    programId: string;
    programKind: MobileProgramKind;
  } | null;
  lifecycleMode: EventMode;
  experiences: readonly MobileExperienceOption[];
  selectedExperience: MobileExperienceKind;
  selectionReason: MobileExperienceSelectionReason;
  homePath: string;
  navigation: readonly MobileNavigationDestination[];
  revision: string;
  refreshAt: string;
}

interface ResolvableEvent {
  source: MobileExperienceEventInput;
  mode: EventMode;
  experiences: readonly MobileExperienceOption[];
}

const ATTENDEE_NAVIGATION: readonly MobileNavigationDestination[] = [
  destination("home", "Home", "/", "home", ["/"]),
  destination("schedule", "Schedule", "/schedule", "calendar", ["/schedule"]),
  destination("my-wvcea", "My WVCEA", "/my-wvcea", "person", ["/my-wvcea", "/my-stuff", "/my-convention"]),
  destination("map", "Map", "/map", "map", ["/map", "/explore"]),
  destination("more", "More", "/more", "more", ["/more", "/updates", "/help"]),
];

const CONVENTION_STAFF_NAVIGATION = (eventSlug: string): readonly MobileNavigationDestination[] => [
  destination("staff-today", "Today", staffPath(eventSlug, "today"), "staff", [staffPath(eventSlug, "today")]),
  destination("staff-schedule", "Schedule", staffPath(eventSlug, "schedule"), "calendar", [staffPath(eventSlug, "schedule")]),
  destination("staff-more", "More", staffPath(eventSlug, "more"), "more", [staffPath(eventSlug, "more")]),
];

const CONVENTION_SCHOOL_NAVIGATION = (eventSlug: string): readonly MobileNavigationDestination[] => [
  destination("school-today", "School", schoolPath(eventSlug, "today"), "school", [schoolPath(eventSlug, "today")]),
  destination("school-faculty", "Faculty", schoolPath(eventSlug, "faculty"), "people", [schoolPath(eventSlug, "faculty")]),
  destination("school-more", "More", schoolPath(eventSlug, "more"), "more", [schoolPath(eventSlug, "more")]),
];

const CONVENTION_PRESENTER_NAVIGATION = (eventSlug: string): readonly MobileNavigationDestination[] => [
  destination("presenter-today", "Today", presenterPath(eventSlug, "today"), "sessions", [presenterPath(eventSlug, "today")]),
  destination("presenter-sessions", "Sessions", presenterPath(eventSlug, "sessions"), "materials", [presenterPath(eventSlug, "sessions")]),
  destination("presenter-more", "More", presenterPath(eventSlug, "more"), "more", [presenterPath(eventSlug, "more")]),
];

const FINE_ARTS_COORDINATOR_NAVIGATION = (eventSlug: string): readonly MobileNavigationDestination[] => [
  destination("fine-arts-today", "Today", fineArtsCoordinatorPath(eventSlug, "today"), "staff", [fineArtsCoordinatorPath(eventSlug, "today")]),
  destination("fine-arts-rooms", "Rooms", fineArtsCoordinatorPath(eventSlug, "rooms"), "room", [fineArtsCoordinatorPath(eventSlug, "rooms")]),
  destination("fine-arts-more", "More", fineArtsCoordinatorPath(eventSlug, "more"), "more", [fineArtsCoordinatorPath(eventSlug, "more")]),
];

const FINE_ARTS_ROOM_MONITOR_NAVIGATION = (eventSlug: string): readonly MobileNavigationDestination[] => [
  destination("fine-arts-room", "Room", fineArtsRoomMonitorPath(eventSlug, "room"), "room", [fineArtsRoomMonitorPath(eventSlug, "room")]),
  destination("fine-arts-room-more", "More", fineArtsRoomMonitorPath(eventSlug, "more"), "more", [fineArtsRoomMonitorPath(eventSlug, "more")]),
];

const FINE_ARTS_CHECK_IN_NAVIGATION = (eventSlug: string): readonly MobileNavigationDestination[] => [
  destination("fine-arts-check-in", "Check-in", fineArtsCheckInPath(eventSlug, "schools"), "people", [fineArtsCheckInPath(eventSlug, "schools")]),
  destination("fine-arts-check-in-more", "More", fineArtsCheckInPath(eventSlug, "more"), "more", [fineArtsCheckInPath(eventSlug, "more")]),
];

const FINE_ARTS_DIRECTOR_NAVIGATION = (eventSlug: string): readonly MobileNavigationDestination[] => [
  destination("fine-arts-school-today", "Today", fineArtsDirectorPath(eventSlug, "today"), "school", [fineArtsDirectorPath(eventSlug, "today")]),
  destination("fine-arts-school-schedule", "Schedule", fineArtsDirectorPath(eventSlug, "schedule"), "calendar", [fineArtsDirectorPath(eventSlug, "schedule")]),
  destination("fine-arts-school-roster", "Roster", fineArtsDirectorPath(eventSlug, "roster"), "people", [fineArtsDirectorPath(eventSlug, "roster")]),
  destination("fine-arts-school-more", "More", fineArtsDirectorPath(eventSlug, "more"), "more", [fineArtsDirectorPath(eventSlug, "more")]),
];

const FINE_ARTS_JUDGE_NAVIGATION = (eventSlug: string): readonly MobileNavigationDestination[] => [
  destination("fine-arts-judge", "Judge", fineArtsJudgePath(eventSlug, "assignments"), "scoring", [fineArtsJudgePath(eventSlug, "assignments")]),
  destination("fine-arts-judge-more", "More", fineArtsJudgePath(eventSlug, "more"), "more", [fineArtsJudgePath(eventSlug, "more")]),
];

const FINE_ARTS_AWARDS_NAVIGATION = (eventSlug: string): readonly MobileNavigationDestination[] => [
  destination("fine-arts-awards", "Awards", fineArtsAwardsPath(eventSlug, "held"), "results", [fineArtsAwardsPath(eventSlug, "held")]),
  destination("fine-arts-awards-more", "More", fineArtsAwardsPath(eventSlug, "more"), "more", [fineArtsAwardsPath(eventSlug, "more")]),
];

const FINE_ARTS_SUPPORT_NAVIGATION = (eventSlug: string): readonly MobileNavigationDestination[] => [
  destination("fine-arts-support", "Support", fineArtsSupportPath(eventSlug, "today"), "help", [fineArtsSupportPath(eventSlug, "today")]),
  destination("fine-arts-support-rooms", "Rooms", fineArtsSupportPath(eventSlug, "rooms"), "room", [fineArtsSupportPath(eventSlug, "rooms")]),
  destination("fine-arts-support-more", "More", fineArtsSupportPath(eventSlug, "more"), "more", [fineArtsSupportPath(eventSlug, "more")]),
];

/**
 * Resolve one mobile presentation from facts that have already passed tenant and authority checks.
 *
 * This module does not load records and does not grant access. Adapters supply current eligible
 * experiences; protected routes remain responsible for authorizing every read and write.
 */
export function resolveMobileExperience(input: MobileExperienceInput): MobileExperienceDecision {
  const events = input.events
    .map((event): ResolvableEvent | null => {
      const experiences = uniqueExperiences(event.experiences)
        .map((candidate) => optionFor(event, candidate))
        .filter((candidate): candidate is MobileExperienceOption => candidate !== null);
      if (experiences.length === 0) return null;
      return { source: event, mode: safeEventMode(input.now, event.windows), experiences };
    })
    .filter((event): event is ResolvableEvent => event !== null);

  const activeTakeovers = events.filter((event) => event.mode === "active" && event.source.takeover.enabled);
  if (activeTakeovers.length > 0) {
    const highest = Math.max(...activeTakeovers.map((event) => event.source.takeover.priority));
    const leading = activeTakeovers.filter((event) => event.source.takeover.priority === highest);
    if (leading.length === 1) {
      const event = leading[0]!;
      if (input.preference?.eventId === event.source.id
        && event.experiences.some((experience) => experience.kind === input.preference?.experience)) {
        return decision(event, input.preference.experience, "explicit", input.now);
      }
      const experience = defaultActiveExperience(event);
      return decision(event, experience.kind, "active_takeover", input.now);
    }
    return attendeeFallback(input.now, "choice_required");
  }

  const preferred = input.preference
    ? events.find((event) => event.mode !== "archive"
      && event.source.id === input.preference?.eventId
      && event.experiences.some((experience) => experience.kind === input.preference?.experience)) ?? null
    : null;
  if (preferred && input.preference) {
    return decision(preferred, input.preference.experience, "explicit", input.now);
  }

  const relevant = [...events].sort(compareEventRelevance)[0];
  if (relevant) {
    const attendee = relevant.experiences.find((experience) => experience.kind === "attendee");
    const chosen = attendee ?? relevant.experiences[0]!;
    return decision(relevant, chosen.kind, "event_relevance", input.now);
  }

  return attendeeFallback(input.now, "attendee_fallback");
}

function optionFor(event: MobileExperienceEventInput, candidate: MobileExperienceCandidate): MobileExperienceOption | null {
  if (candidate.kind === "attendee") {
    return { kind: candidate.kind, label: candidate.label, homePath: "/", navigation: ATTENDEE_NAVIGATION };
  }
  if (event.programKind === "convention" && candidate.kind === "staff") {
    const navigation = CONVENTION_STAFF_NAVIGATION(event.slug);
    return { kind: candidate.kind, label: candidate.label, homePath: navigation[0]!.path, navigation };
  }
  if (event.programKind === "convention" && candidate.kind === "school") {
    const navigation = CONVENTION_SCHOOL_NAVIGATION(event.slug);
    return { kind: candidate.kind, label: candidate.label, homePath: navigation[0]!.path, navigation };
  }
  if (event.programKind === "convention" && candidate.kind === "presenter") {
    const navigation = CONVENTION_PRESENTER_NAVIGATION(event.slug);
    return { kind: candidate.kind, label: candidate.label, homePath: navigation[0]!.path, navigation };
  }
  if (event.programKind === "fine_arts" && candidate.kind === "fine_arts_coordinator") {
    const navigation = FINE_ARTS_COORDINATOR_NAVIGATION(event.slug);
    return { kind: candidate.kind, label: candidate.label, homePath: navigation[0]!.path, navigation };
  }
  if (event.programKind === "fine_arts" && candidate.kind === "fine_arts_room_monitor") {
    const navigation = FINE_ARTS_ROOM_MONITOR_NAVIGATION(event.slug);
    return { kind: candidate.kind, label: candidate.label, homePath: navigation[0]!.path, navigation };
  }
  if (event.programKind === "fine_arts" && candidate.kind === "fine_arts_check_in") {
    const navigation = FINE_ARTS_CHECK_IN_NAVIGATION(event.slug);
    return { kind: candidate.kind, label: candidate.label, homePath: navigation[0]!.path, navigation };
  }
  if (event.programKind === "fine_arts" && candidate.kind === "fine_arts_director") {
    const navigation = FINE_ARTS_DIRECTOR_NAVIGATION(event.slug);
    return { kind: candidate.kind, label: candidate.label, homePath: navigation[0]!.path, navigation };
  }
  if (event.programKind === "fine_arts" && candidate.kind === "fine_arts_judge") {
    const navigation = FINE_ARTS_JUDGE_NAVIGATION(event.slug);
    return { kind: candidate.kind, label: candidate.label, homePath: navigation[0]!.path, navigation };
  }
  if (event.programKind === "fine_arts" && candidate.kind === "fine_arts_awards") {
    const navigation = FINE_ARTS_AWARDS_NAVIGATION(event.slug);
    return { kind: candidate.kind, label: candidate.label, homePath: navigation[0]!.path, navigation };
  }
  if (event.programKind === "fine_arts" && candidate.kind === "fine_arts_support") {
    const navigation = FINE_ARTS_SUPPORT_NAVIGATION(event.slug);
    return { kind: candidate.kind, label: candidate.label, homePath: navigation[0]!.path, navigation };
  }
  // A role does not enter the shell until its mobile routes and authority checks are complete.
  return null;
}

function decision(
  event: ResolvableEvent,
  experienceKind: MobileExperienceKind,
  selectionReason: MobileExperienceSelectionReason,
  now: Date,
): MobileExperienceDecision {
  const selected = event.experiences.find((experience) => experience.kind === experienceKind)
    ?? event.experiences.find((experience) => experience.kind === "attendee")
    ?? event.experiences[0]!;
  return {
    event: {
      id: event.source.id,
      slug: event.source.slug,
      name: event.source.name,
      programId: event.source.programId,
      programKind: event.source.programKind,
    },
    lifecycleMode: event.mode,
    experiences: event.experiences,
    selectedExperience: selected.kind,
    selectionReason,
    homePath: selected.homePath,
    navigation: selected.navigation,
    revision: `mobile-v2:${event.source.id}:${event.source.takeover.revision}:${selected.kind}`,
    refreshAt: new Date(now.getTime() + 5 * 60_000).toISOString(),
  };
}

function attendeeFallback(now: Date, selectionReason: MobileExperienceSelectionReason): MobileExperienceDecision {
  const attendee: MobileExperienceOption = {
    kind: "attendee",
    label: "My WVCEA",
    homePath: "/",
    navigation: ATTENDEE_NAVIGATION,
  };
  return {
    event: null,
    lifecycleMode: "quiet",
    experiences: [attendee],
    selectedExperience: "attendee",
    selectionReason,
    homePath: attendee.homePath,
    navigation: attendee.navigation,
    revision: "mobile-v2:fallback:attendee",
    refreshAt: new Date(now.getTime() + 5 * 60_000).toISOString(),
  };
}

function defaultActiveExperience(event: ResolvableEvent): MobileExperienceOption {
  if (event.source.programKind === "convention") {
    const staff = event.experiences.find((experience) => experience.kind === "staff");
    if (staff) return staff;
  }
  return event.experiences.find((experience) => experience.kind === "attendee") ?? event.experiences[0]!;
}

function compareEventRelevance(left: ResolvableEvent, right: ResolvableEvent): number {
  const rank: Readonly<Record<EventMode, number>> = {
    active: 0,
    preparation: 1,
    "follow-through": 2,
    quiet: 3,
    archive: 4,
  };
  return rank[left.mode] - rank[right.mode]
    || left.source.startsOn.localeCompare(right.source.startsOn)
    || left.source.id.localeCompare(right.source.id);
}

function safeEventMode(now: Date, windows: EventWindows | null): EventMode {
  if (!windows) return "quiet";
  try {
    return determineEventMode(now, windows);
  } catch {
    return "quiet";
  }
}

function uniqueExperiences(candidates: readonly MobileExperienceCandidate[]): readonly MobileExperienceCandidate[] {
  const seen = new Set<MobileExperienceKind>();
  return candidates.filter((candidate) => {
    if (seen.has(candidate.kind)) return false;
    seen.add(candidate.kind);
    return true;
  });
}

function staffPath(eventSlug: string, destination: "today" | "schedule" | "more"): string {
  return `/mobile/events/${encodeURIComponent(eventSlug)}/staff/${destination}`;
}

function schoolPath(eventSlug: string, destination: "today" | "faculty" | "more"): string {
  return `/mobile/events/${encodeURIComponent(eventSlug)}/school/${destination}`;
}

function presenterPath(eventSlug: string, destination: "today" | "sessions" | "more"): string {
  return `/mobile/events/${encodeURIComponent(eventSlug)}/presenter/${destination}`;
}

function fineArtsCoordinatorPath(eventSlug: string, destination: "today" | "rooms" | "more"): string {
  return `/mobile/events/${encodeURIComponent(eventSlug)}/fine-arts/coordinator/${destination}`;
}

function fineArtsRoomMonitorPath(eventSlug: string, destination: "room" | "more"): string {
  return `/mobile/events/${encodeURIComponent(eventSlug)}/fine-arts/room-monitor/${destination}`;
}

function fineArtsCheckInPath(eventSlug: string, destination: "schools" | "more"): string {
  return `/mobile/events/${encodeURIComponent(eventSlug)}/fine-arts/check-in/${destination}`;
}

function fineArtsDirectorPath(eventSlug: string, destination: "today" | "schedule" | "roster" | "more"): string {
  return `/mobile/events/${encodeURIComponent(eventSlug)}/fine-arts/director/${destination}`;
}

function fineArtsJudgePath(eventSlug: string, destination: "assignments" | "more"): string {
  return `/mobile/events/${encodeURIComponent(eventSlug)}/fine-arts/judge/${destination}`;
}

function fineArtsAwardsPath(eventSlug: string, destination: "held" | "more"): string {
  return `/mobile/events/${encodeURIComponent(eventSlug)}/fine-arts/awards/${destination}`;
}

function fineArtsSupportPath(eventSlug: string, destination: "today" | "rooms" | "more"): string {
  return `/mobile/events/${encodeURIComponent(eventSlug)}/fine-arts/support/${destination}`;
}

function destination(
  id: string,
  label: string,
  path: string,
  icon: MobileIconKey,
  routePrefixes: readonly string[],
): MobileNavigationDestination {
  return { id, label, path, icon, routePrefixes };
}
